import Head from 'next/head'
import { useRouter } from 'next/router'
import { FunctionComponent, useContext } from 'react'
import styled, { DefaultTheme, ThemeContext } from 'styled-components'

import PhotoCarousel from 'modules/PhotoCarousel'

import Caption from 'design-system/Typography/Caption/Caption'

import Breadcrumbs from 'components/Breadcrumbs'
import RoundedButton from 'components/RoundedButton'
import FlaredArrowShort from 'components/SVG/FlaredArrowShort'
import PhotoIcon from 'components/SVG/Photo'
import VideoTourIcon from 'components/SVG/VideoCamera'
import VirtualTourIcon from 'components/SVG/compound/VirtualTour'
import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'
import { ImageTypeEnum, getImageUrl } from 'services/images'

import { formatAddress } from 'utils/listingHelpers'
import { isClosedOrSold } from 'utils/statusHelpers'

import * as T from 'types'
import { BreadcrumbEnum } from 'types/enums'

import PreviewBanner from './PreviewBanner'
import PreviewDetails from './PreviewDetails'
import Sections from './Sections'
import Sidebar from './Sidebar'

interface IIsInListingFlowProp {
  isInListingFlow?: boolean
}
interface IIsInMapSearchModalProp {
  inMapSearchModal?: boolean
}
const Container = styled.div<IIsInListingFlowProp & IIsInMapSearchModalProp>`
  position: relative;
  margin: 0 auto;
  padding-bottom: 0;
  min-width: 0px;
  max-width: ${props =>
    props.isInListingFlow
      ? `${
        props.theme.metrics.publicListingMaxWidth - props.theme.metrics.publicListingSidebarWidth
      }px`
      : `${props.theme.metrics.publicListingMaxWidth}px`};
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    ${props => !props.isInListingFlow && `padding: 0 ${props.theme.metrics.publicListingGutter}px;`}
  }

  @media (max-width: ${props => props.theme.metrics.desktopLarge}px) {
    ${props => props.inMapSearchModal && `max-width: ${props.theme.metrics.desktop}px;`}
  }
`

const ListingContainer = styled.div`
  width: 100%;
  flex-direction: row;
  display: flex;
`

const MainColumnContainer = styled.div`
  box-sizing: border-box;
  margin: 0px;
  min-width: 0px;
  flex: 1 1 auto;
  max-width: 100%;
  overflow: hidden;
`

const BreadcrumbsContainer = styled.div`
  height: 54px;
  display: flex;
  align-items: center;
`

interface IIsInViewListingProps {
  isInViewListing?: boolean
}
const TourButtonsContainer = styled.div<IIsInViewListingProps>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    flex-direction: row;
  }
`

const ButtonAnchor = styled.a`
  text-decoration: none;
  margin-bottom: 10px;
`

interface IListingDetailsProps {
  gutterPadding?: boolean
}
const ListingDetailsContainer = styled.div<IListingDetailsProps>`
  display: flex;
  flex-direction: column;
  margin: 0px;
  min-width: 0px;
  padding: 0 10px;
`

const ResponsiveDesktopContainer = styled.div`
  display: none;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: flex;
  }
`

const ResponsiveMobileContainer = styled.div`
  display: flex;
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: none;
  }
`

const NoPhotos = styled.div`
  width: 100%;
  height: 556px;
  background: ${props => props.theme.colors.grey};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 33px;
  color: ${props => props.theme.colors.purple};
`

const ActionRow = styled.div`
  background: ${props => props.theme.colors.white};
  display: flex;
  padding: 0 10px;

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    padding: 0;
  }
`

const ActionButtonContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const LeftFlaredArrowShort = styled(FlaredArrowShort)`
  transform: rotate(180deg);
`

interface IRenderPhotoSection {
  (
    listing: T.IListing,
    photos: T.IPhoto[],
    theme: DefaultTheme,
    isInListingFlow?: boolean,
  ): JSX.Element
}

const renderPhotoSection: IRenderPhotoSection = (listing, photos, theme, isInListingFlow) => {
  if (!photos.length) {
    return (
      <NoPhotos>
        <PhotoIcon
          width={82}
          height={66}
          fill={theme.colors.transparentPurpleAlpha25}
        />
        <Spacer size={16} />
        No Photos Added
      </NoPhotos>
    )
  }

  if (isClosedOrSold(listing)) {
    const photo = photos[0]
    const src = getImageUrl(listing._id, photo, ImageTypeEnum.ViewListingSlide)
    return (
      <img
        alt={`Exterior of ${listing.street}`}
        src={src}
      />
    )
  }

  // at least one photo exists and the listing is not sold
  return (
    <PhotoCarousel
      listing={listing}
      photos={photos}
      isViewListingPage={!isInListingFlow}
    />
  )
}

interface IProps {
  isPreview?: boolean
  isInListingFlow?: boolean
  listing: T.IListing
  photos: T.IPhoto[]
  inMapSearchModal?: boolean
  onClose?: () => void
}

const ViewListing: FunctionComponent<IProps> = ({
  listing,
  photos = [],
  isPreview,
  isInListingFlow,
  inMapSearchModal = false,
  onClose,
}) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()

  const isMobile = width && width <= theme.metrics.phone

  const renderVirtualTourIcon = () => (
    <VirtualTourIcon
      width={35}
      height={40}
    />
  )

  const renderVideoTourIcon = () => (
    <VideoTourIcon
      width={35}
      height={35}
    />
  )

  const {
    query: { isSold },
  } = useRouter()

  const setBreadcrumbName = (): BreadcrumbEnum => {
    if (isPreview) return BreadcrumbEnum.Dashboard

    return isSold ? BreadcrumbEnum.RecentSales : BreadcrumbEnum.HomesForSale
  }

  const photoSection = renderPhotoSection(listing, photos, theme, isInListingFlow)

  return (
    <>
      <Head>
        <script
          type="text/javascript"
          src="https://platform-api.sharethis.com/js/sharethis.js#property=601dcc829afb3500116211e1&product=custom-share-buttons"
          async
          defer
        />
      </Head>
      <Container
        isInListingFlow={isInListingFlow}
        inMapSearchModal={inMapSearchModal}
      >
        {!isInListingFlow && !inMapSearchModal && (
          <BreadcrumbsContainer>
            <Breadcrumbs
              name={setBreadcrumbName()}
              listingName={formatAddress(listing, true)}
              noMargin
            />
          </BreadcrumbsContainer>
        )}
        {inMapSearchModal && onClose && (
          <>
            {/* This is the new design that replaces Breadcrumbs, but it's coming to just the map
                search to start */}
            <Spacer size={theme.spacings[24]} />
            <ActionRow>
              <ActionButtonContainer onClick={onClose}>
                <LeftFlaredArrowShort
                  width={11}
                  height={11}
                />
                <Spacer size={8} />
                <Caption>BACK</Caption>
              </ActionButtonContainer>
              {/* Share Button coming soon */}
              {/* <ActionButtonContainer>
                <ShareButton />
              </ActionButtonContainer> */}
            </ActionRow>
            <Spacer size={26} />
          </>
        )}
        {isPreview && <PreviewBanner gutterPadding={isInListingFlow} />}
        <ListingContainer>
          <MainColumnContainer>
            <ListingDetailsContainer gutterPadding={isPreview && isInListingFlow}>
              {photoSection}
              <Spacer size={5} />
              <TourButtonsContainer isInViewListing={!isInListingFlow}>
                {listing.videos3d.length > 0 && (
                  <ButtonAnchor href={listing.videos3d[0]}>
                    <RoundedButton
                      text="3D Virtual Tour"
                      background={theme.colors.white}
                      borderColor={theme.colors.grey6}
                      fontSize={isMobile ? 15 : 17}
                      height={isMobile ? 40 : 52}
                      padding="0 15px"
                      radius={4}
                      renderLeftIcon={renderVirtualTourIcon}
                      bold
                    />
                  </ButtonAnchor>
                )}
                <Spacer
                  size={10}
                  hideMobile
                />
                {listing.videos.length > 0 && (
                  <ButtonAnchor href={listing.videos[0]}>
                    <RoundedButton
                      href={listing.videos[0]}
                      text="Video Tour"
                      background={theme.colors.white}
                      borderColor={theme.colors.grey6}
                      fontSize={isMobile ? 15 : 17}
                      height={isMobile ? 40 : 52}
                      padding="0 15px"
                      radius={4}
                      renderLeftIcon={renderVideoTourIcon}
                      bold
                    />
                  </ButtonAnchor>
                )}
              </TourButtonsContainer>
              <Spacer size={5} />
              {!isInListingFlow && (
                <ResponsiveMobileContainer>
                  <Sidebar listing={listing} />
                </ResponsiveMobileContainer>
              )}
              {isPreview && isInListingFlow && <PreviewDetails listing={listing} />}
            </ListingDetailsContainer>
            <Sections
              listing={listing}
              displayMap={!inMapSearchModal}
            />
            {inMapSearchModal && <Spacer />}
          </MainColumnContainer>
          {!isInListingFlow && (
            <ResponsiveDesktopContainer>
              <Spacer size={theme.metrics.publicListingSpacer} />
              <Sidebar listing={listing} />
            </ResponsiveDesktopContainer>
          )}
        </ListingContainer>
      </Container>
    </>
  )
}

export default ViewListing
